import {
  IApiModelSearchable,
  IApiModelType,
  IDynamicModelOptions,
} from '../../models';

/**
 * Pure API-model mapping helpers, extracted from DynamicItemBaseComponent so the base
 * stays under the 500-line file gate (WI 31903 CD-plumbing). No component state and no
 * behavior change: the base methods delegate here and keep their overridable public surface.
 */

/**
 * Map raw API items onto IApiModelSearchable using the control's api.model field mapping, then
 * drop any option listed in the control's `excludeValueIds` (US 35748).
 * Mirrors the previous DynamicItemBaseComponent.apiModelMapper body verbatim.
 */
export function mapApiModel(
  res: Array<IApiModelSearchable>,
  options: IDynamicModelOptions | undefined
): Array<IApiModelSearchable> {
  return excludeApiModelIds(
    mapAllApiModel(res, options?.api?.model),
    options?.excludeValueIds
  );
}

function mapAllApiModel(
  res: Array<IApiModelSearchable>,
  model: IApiModelType | undefined
): Array<IApiModelSearchable> {
  if (res && model) {
    // model property mapping
    return res.map((item) => {
      const id = item[model.id] || item.Id || item.id;
      const name = item[model.name] || item.Name || item.name;
      const description =
        item[model.description] || item.Description || item.description;
      const active =
        item[model.active]?.toString() || item.Active || item.active;
      const result: IApiModelSearchable = {};
      result.id = id;
      result.Id = id;
      result.name = name;
      result.Name = name;
      result.description = description;
      result.Description = description;
      result.active = active;
      result.Active = active;
      result.altId = item[model.altId];
      return result;
    });
  } else if (res) {
    return res as Array<unknown>;
  } else {
    return [];
  }
}

/**
 * Case-insensitive filter over already-mapped items by id/name/description.
 * Mirrors the previous DynamicItemBaseComponent.filterData filter callback verbatim.
 */
export function filterApiModel(
  mapped: Array<IApiModelSearchable>,
  searchString?: string
): Array<IApiModelSearchable> {
  return mapped.filter((item) => {
    if (searchString) {
      // TODO: remove mixed Pascal and camel casing on arch branch
      return (
        (item.id || item.Id)
          ?.toString()
          .toLowerCase()
          .indexOf(searchString.toLowerCase()) > -1 ||
        (item.name || item.Name)
          ?.toString()
          .toLowerCase()
          .indexOf(searchString.toLowerCase()) > -1 ||
        (item.description || item.Description)
          ?.toString()
          ?.toLowerCase()
          .indexOf(searchString.toLowerCase()) > -1
      );
    } else {
      return true;
    }
  });
}

/**
 * Drop options whose mapped id is in `excludeIds` (case-insensitive, GUID casing differs between
 * the API and the TS enums). No list, or an empty one, returns the items untouched; null items
 * become an empty list. US 35748.
 */
export function excludeApiModelIds(
  mapped: Array<IApiModelSearchable> | null | undefined,
  excludeIds: readonly string[] | null | undefined
): Array<IApiModelSearchable> {
  if (!mapped) {
    return [];
  }
  if (!excludeIds?.length) {
    return mapped;
  }
  const excluded = new Set(excludeIds.map((id) => id.toLowerCase()));
  return mapped.filter((item) => {
    const id = (item.id ?? item.Id)?.toString().toLowerCase();
    return !id || !excluded.has(id);
  });
}
